import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  WorkflowResponse,
  WorkflowCreateRequest,
  WorkflowUpdateRequest,
  WorkflowFilterRequest,
  WorkflowDuplicateRequest,
  WorkflowTemplateCreateRequest
} from '../models/workflow.model';
import { PageResponse } from '../../../../pages/auth-pages/models/user.model';
import { WorkflowNodeResponse } from '../models/workflow-builder.model';

@Injectable({ providedIn: 'root' })
export class WorkflowApiService {
  private readonly apiUrl = `${environment.apiUrl}api/workflows`;

  constructor(private http: HttpClient) {}

  getWorkflows(filters: WorkflowFilterRequest): Observable<PageResponse<WorkflowResponse> | WorkflowResponse[]> {
    let params = new HttpParams()
      .set('businessId', filters.businessId.toString())
      .set('page', (filters.page ?? 0).toString())
      .set('size', (filters.size ?? 20).toString());

    if (filters.status)      params = params.set('status', filters.status);
    if (filters.objectType)  params = params.set('objectType', filters.objectType);
    if (filters.folderId)    params = params.set('idFolder', filters.folderId.toString());
    if (filters.ownerId)     params = params.set('idOwnerUser', filters.ownerId.toString());
    if (filters.searchTerm)  params = params.set('search', filters.searchTerm);

    return this.http.get<PageResponse<WorkflowResponse> | WorkflowResponse[]>(this.apiUrl, { params });
  }

  getById(id: number): Observable<WorkflowResponse> {
    return this.http.get<WorkflowResponse>(`${this.apiUrl}/${id}`);
  }

  create(request: WorkflowCreateRequest): Observable<WorkflowResponse> {
    return this.http.post<WorkflowResponse>(this.apiUrl, request);
  }

  update(id: number, request: WorkflowUpdateRequest): Observable<WorkflowResponse> {
    return this.http.put<WorkflowResponse>(`${this.apiUrl}/${id}`, request);
  }

  activate(id: number): Observable<WorkflowResponse> {
    return this.http.patch<WorkflowResponse>(`${this.apiUrl}/${id}/activate`, {});
  }

  pause(id: number): Observable<WorkflowResponse> {
    return this.http.patch<WorkflowResponse>(`${this.apiUrl}/${id}/pause`, {});
  }

  /**
   * Archiva un workflow.
   *
   * Si tiene inscripciones en curso el backend responde 400 con
   * `WORKFLOW_HAS_ACTIVE_ENROLLMENTS` y `details.activeEnrollments`. Reintentar con `force = true`
   * cancela esas inscripciones (queda auditado) y archiva.
   */
  archive(id: number, force = false): Observable<WorkflowResponse> {
    const params = force ? new HttpParams().set('force', 'true') : undefined;
    return this.http.patch<WorkflowResponse>(`${this.apiUrl}/${id}/archive`, {}, { params });
  }

  restore(id: number): Observable<WorkflowResponse> {
    return this.http.patch<WorkflowResponse>(`${this.apiUrl}/${id}/restore`, {});
  }

  duplicate(id: number, request: WorkflowDuplicateRequest): Observable<WorkflowResponse> {
    return this.http.post<WorkflowResponse>(`${this.apiUrl}/${id}/duplicate`, request);
  }

  saveAsTemplate(id: number, request: WorkflowTemplateCreateRequest): Observable<WorkflowResponse> {
    return this.http.post<WorkflowResponse>(`${this.apiUrl}/${id}/template`, request);
  }

  /**
   * INC_CRM_020 (4) inc12 — "la biblioteca de plantillas aparece vacía".
   *
   * `GET /api/workflows/templates` devuelve una PÁGINA (`{ content, totalElements, … }`), no un
   * array. Al tiparlo aquí como `WorkflowResponse[]` el objeto de página llegaba tal cual al
   * componente, que lo asignaba a `templates` y hacía `@for` sobre él: iterar un objeto plano no
   * produce ninguna fila, así que el modal salía vacío aunque la plantilla se hubiera guardado
   * bien. Se pide un tamaño amplio porque el modal no pagina.
   */
  getTemplates(businessId?: number): Observable<WorkflowResponse[]> {
    let params = new HttpParams().set('size', '200');
    if (businessId !== undefined) params = params.set('businessId', businessId.toString());
    return this.http
      .get<PageResponse<WorkflowResponse> | WorkflowResponse[]>(`${this.apiUrl}/templates`, { params })
      .pipe(map(res => (Array.isArray(res) ? res : res?.content ?? [])));
  }

  /**
   * INC_CRM_020 (4) inc8 — borrado físico. Es POST y no DELETE porque lleva cuerpo: el backend
   * exige la contraseña del usuario autenticado y que el workflow NO esté activo.
   * Errores: 401 contraseña incorrecta; 400 `WORKFLOW_DELETE_REQUIRES_INACTIVE` si está activo.
   */
  deleteWithPassword(id: number, password: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${id}/delete`, { password });
  }

  createFromTemplate(templateId: number, request: WorkflowCreateRequest): Observable<WorkflowResponse> {
    return this.http.post<WorkflowResponse>(`${this.apiUrl}/from-template/${templateId}`, request);
  }

  listNodes(workflowId: number, versionId?: number): Observable<WorkflowNodeResponse[]> {
    let params = new HttpParams();
    if (versionId) params = params.set('versionId', versionId.toString());
    return this.http.get<WorkflowNodeResponse[]>(`${this.apiUrl}/${workflowId}/nodes`, { params });
  }
}
